import { computed, inject, Injectable, Signal } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { selectSelectedAccountId } from '@mkp/account/state';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';

const { routes } = AppConfig;

/**
 * Builds a workspace-prefixed route array for use with Router.navigate() or [routerLink].
 * @param accountId - The workspace/account ID
 * @param paths - The route segments to append after workspace/:workspaceId
 * @returns Array of route segments starting with /workspace/:workspaceId
 */
export const buildWorkspaceRoute = (accountId: string, ...paths: string[]): string[] => [
  '/',
  routes.workspace,
  accountId,
  ...paths,
];

/**
 * Builds a workspace-prefixed route path string.
 * @param accountId - The workspace/account ID
 * @param paths - The route segments to append after workspace/:workspaceId
 * @returns String path like '/workspace/:workspaceId/credit-management'
 */
export const buildWorkspaceRoutePath = (accountId: string, ...paths: string[]): string =>
  `/${routes.workspace}/${accountId}${paths.length ? '/' + paths.join('/') : ''}`;

/**
 * Injectable service for building workspace-prefixed routes.
 *
 * @example
 * ```typescript
 * private readonly workspaceRoute = inject(WorkspaceRouteService);
 *
 * // For RouterLink - returns a signal
 * creditManagementLink = this.workspaceRoute.link(routes.creditManagement);
 * // Template: [routerLink]="creditManagementLink()"
 *
 * // For programmatic navigation
 * goToPurchaseCredits() {
 *   this.workspaceRoute.navigate(
 *     [routes.creditManagement, routes.purchaseCredits],
 *     { queryParams: { walletId: 'some-id' } }
 *   );
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceRouteService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly accountId = toSignal(this.store.select(selectSelectedAccountId));

  /**
   * Creates a signal that returns a workspace-prefixed route array.
   * Use this for [routerLink] bindings in templates.
   */
  link(...paths: string[]): Signal<string[]> {
    return computed(() => {
      const id = this.accountId();
      return id ? buildWorkspaceRoute(id, ...paths) : [];
    });
  }

  /**
   * Navigates to a workspace-prefixed route.
   * Returns a Promise that resolves after navigation, similar to Router.navigate().
   */
  navigate(paths: string | string[], extras?: NavigationExtras): Promise<boolean> {
    const route = this.buildRoute(paths);
    return route ? this.router.navigate(route, extras) : Promise.resolve(false);
  }

  /**
   * Creates a UrlTree for workspace-prefixed routes.
   * Use this in guards or resolvers.
   */
  createUrlTree(...paths: string[]) {
    const route = this.buildRoute(paths);
    return route ? this.router.createUrlTree(route) : null;
  }

  /**
   * Serializes a workspace-prefixed route to a URL string.
   * @param paths - The route segments to append after workspace/:workspaceId
   * @returns Serialized URL string or null if no account is selected
   */
  serializeUrl(...paths: string[]) {
    const urlTree = this.createUrlTree(...paths);
    return urlTree ? this.router.serializeUrl(urlTree) : null;
  }

  private buildRoute(paths: string | string[]): string[] | null {
    const id = this.accountId();
    if (!id) {
      return null;
    }
    const pathArray = Array.isArray(paths) ? paths : [paths];
    return buildWorkspaceRoute(id, ...pathArray);
  }
}
